import { authEndpoints } from '@/api/endpoints';
import { API_CONFIG } from '@/config/apiConfig';
import { MIN_LEN } from '@/constants';
import { isJWTExpired } from '@/store/utils/jwtDecoder';
import type { RefreshResponseDTO } from '@/types/DTOs';
import {
  clearAuthTokens,
  getRefreshToken,
  getSessionToken,
  setRefreshToken,
  setSessionToken,
} from '@/utils/sessionStorage';

let refreshInFlightPromise: Promise<boolean> | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getOptionalString(record: Record<string, unknown>, key: 'sessionToken' | 'refreshToken'): string | undefined {
  return typeof record[key] === 'string' ? record[key] : undefined;
}

function parseRefreshPayload(value: unknown): RefreshResponseDTO['payload'] | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null) {
    return null;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  return {
    sessionToken: getOptionalString(value, 'sessionToken'),
    refreshToken: getOptionalString(value, 'refreshToken'),
  };
}

function parseRefreshResponse(value: unknown): RefreshResponseDTO | null {
  if (!isRecord(value)) {
    return null;
  }

  return {
    payload: parseRefreshPayload(value.payload),
  };
}

export async function refreshSessionTokens(baseURL: string = API_CONFIG.baseURL): Promise<boolean> {
  if (refreshInFlightPromise !== null) {
    return await refreshInFlightPromise;
  }

  refreshInFlightPromise = (async() => {
    const refreshToken = getRefreshToken();
    if (refreshToken === null || refreshToken.trim().length <= MIN_LEN) {
      return false;
    }

    const refreshUrl = new URL(`${baseURL}${authEndpoints.refreshToken}`).toString();
    const refreshHeaders = { ...API_CONFIG.defaultHeaders, 'Content-Type': 'application/json' };
    delete (refreshHeaders as Record<string, unknown>).Authorization;

    const refreshResponse = await fetch(refreshUrl, {
      method: 'POST',
      headers: refreshHeaders,
      credentials: 'include',
      body: JSON.stringify({ refreshToken }),
    });

    const refreshJson = await (async(): Promise<RefreshResponseDTO | null> => {
      try {
        return parseRefreshResponse(await refreshResponse.json());
      } catch {
        return null;
      }
    })();

    if (!refreshResponse.ok) {
      return false;
    }

    const sessionToken = refreshJson?.payload?.sessionToken;
    const nextRefreshToken = refreshJson?.payload?.refreshToken;

    if (
      typeof sessionToken !== 'string' ||
      sessionToken.trim().length <= MIN_LEN ||
      typeof nextRefreshToken !== 'string' ||
      nextRefreshToken.trim().length <= MIN_LEN
    ) {
      return false;
    }

    setSessionToken(sessionToken);
    setRefreshToken(nextRefreshToken);
    return true;
  })();

  try {
    return await refreshInFlightPromise;
  } finally {
    refreshInFlightPromise = null;
  }
}

export async function ensureValidSessionToken(baseURL: string = API_CONFIG.baseURL): Promise<string | null> {
  const sessionToken = getSessionToken();
  if (sessionToken !== null && !isJWTExpired(sessionToken)) {
    return sessionToken;
  }

  const refreshed = await refreshSessionTokens(baseURL);
  if (!refreshed) {
    clearAuthTokens();
    return null;
  }

  return getSessionToken();
}
